import { Pipe, PipeTransform } from '@angular/core';

@Pipe({
  name: 'jsonFormat'
})
export class JsonFormatPipe implements PipeTransform {
  transform(value: any): string {
    if (!value) {
      return '';
    }

    let parsed = value;
    
    // If value is a string, try to clean and parse it
    if (typeof value === 'string') {
      try {
        // First, try to clean up common malformed JSON patterns
        const cleaned = this.cleanJsonString(value);
        parsed = JSON.parse(cleaned);
      } catch (e) {
        // If parsing fails, try to extract JSON from the string
        const extracted = this.extractJsonFromString(value);
        if (extracted) {
          try {
            parsed = JSON.parse(extracted);
          } catch (e2) {
            // If all parsing fails, return a cleaned version of the original string
            return this.formatAsReadableText(value);
          }
        } else {
          return this.formatAsReadableText(value);
        }
      }
    }

    try {
      // Format the parsed object with proper indentation
      const formatted = JSON.stringify(parsed, null, 2);
      return this.addSyntaxHighlighting(formatted);
    } catch (error) {
      return 'Invalid JSON';
    }
  }

  private cleanJsonString(str: string): string {
    return str
      // Remove common malformed prefixes like "key">"
      .replace(/^"[^"]*">"/, '')
      // Remove trailing quotes and extra characters
      .replace(/"\s*$/, '')
      // Fix escaped quotes that shouldn't be escaped
      .replace(/\\"/g, '"')
      // Fix newline escapes
      .replace(/\\n/g, '\n')
      // Remove any remaining escape sequences that might be malformed
      .replace(/\\+/g, '\\')
      .trim();
  }

  private extractJsonFromString(str: string): string | null {
    // Try to find JSON object in the string
    const jsonMatch = str.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      return this.cleanJsonString(jsonMatch[0]);
    }
    return null;
  }

  private formatAsReadableText(str: string): string {
    // For non-JSON strings, format them in a readable way
    return str
      .replace(/\\n/g, '<br>')
      .replace(/\\"/g, '"')
      .replace(/\\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  private addSyntaxHighlighting(json: string): string {
    // Escape HTML characters first
    let html = json
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    // Highlight strings (but be careful not to double-escape)
    html = html.replace(/"([^"\\]*(?:\\.[^"\\]*)*)"/g, '<span class="string">"$1"</span>');
    
    // Highlight numbers
    html = html.replace(/\b(-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)\b/g, '<span class="number">$1</span>');
    
    // Highlight booleans
    html = html.replace(/\b(true|false)\b/g, '<span class="boolean">$1</span>');
    
    // Highlight null
    html = html.replace(/\bnull\b/g, '<span class="null">null</span>');
    
    // Highlight keys (property names, not values)
    html = html.replace(/(^|\s)(<span class="string">"([^"]+)"<\/span>)(?=:\s)/gm, '$1<span class="key">$2</span>');
    
    return html;
  }
} 